import { fromEvent, map } from "rxjs";
import { emailAddress as emailAddressPattern } from "constants/regexExpressions";
import validateEmailAddress from "../apis/validateEmailDomain";

export default class EmailInput {
  constructor() {
    this.valueInput = '';
    this.errorMessageId = 'nhs_ErrorForEmail';
    this.id = 'MailAddress';
    this.errorCssClass = 'error';
    this.labelClassName = 'nhs-u-text--visually-hidden';
    this.fieldClassName = 'form__input';
    this.errorClassName = 'form__error';
    this.labelText = 'Email Address';
  }

  set value(newValue){
    this.valueInput = newValue || '';
  }

  get value(){
    return this.valueInput;
  }

  /**
   * Overrides the CSS classes used by getHtml(). Defaults (set in the constructor) match every
   * existing consumer of this component; only screens that want different styling need to call
   * this — see GetMoreInformationForm (NHS20-8178) for the first caller.
   * @param {Object} classNames
   * @param {string} [classNames.labelClassName]
   * @param {string} [classNames.fieldClassName]
   * @param {string} [classNames.errorClassName]
   */
  setClassNames({ labelClassName, fieldClassName, errorClassName } = {}) {
    if (labelClassName !== undefined) {this.labelClassName = labelClassName;}
    if (fieldClassName !== undefined) {this.fieldClassName = fieldClassName;}
    if (errorClassName !== undefined) {this.errorClassName = errorClassName;}
    return this;
  }

  /**
   * Overrides the visible label text. Default (set in the constructor) matches every existing
   * consumer of this component; only screens that want different copy need to call this — see
   * GetMoreInformationForm (NHS20-8178) for the first caller.
   * @param {string} text
   */
  setLabelText(text) {
    this.labelText = text;
    return this;
  }

  /**
   * Overrides the input element's id used by getHtml()/getNode(). Default (set in the constructor)
   * matches every existing consumer of this component; a screen that can render alongside another
   * instance of this component on the same page (e.g. a persistent sidebar form) must override this
   * to avoid a duplicate-id collision — see GetMoreInformationForm (NHS20-8178) for the first caller.
   * @param {string} id
   */
  setInputId(id) {
    this.id = id;
    return this;
  }

  /**
   * Overrides the error-message element's id used by getHtml(). Default (set in the constructor)
   * matches every existing consumer of this component; a screen that can render alongside another
   * instance of this component on the same page (e.g. a persistent sidebar form) must override this
   * to avoid a duplicate-id collision — see GetMoreInformationForm (NHS20-8178) for the first caller.
   * @param {string} errorMessageId
   */
  setErrorMessageId(errorMessageId) {
    this.errorMessageId = errorMessageId;
    return this;
  }

  getHtml() {
    return `<label for="${this.id}" class="${this.labelClassName}">${this.labelText}</label><input data-qa="email-input" data-segment-identity-data="email" id="${this.id}" class="${this.fieldClassName}"
                  placeholder="*Email Address" maxlength="50" name="email" autocomplete="email"
                  type="email" required value="${this.value}" data-form-email-input></input><span id="${this.errorMessageId}" class="${this.errorClassName}" hidden>
                           Please enter a valid email address
                         </span>`;
  }

  getNode() {
    return document.getElementById(this.id);
  }

  /**
   * getOnChangeEvent: On input change map its value to the storage property.
   */
  getOnChangeEvent() {
    return fromEvent(this.getNode(), 'change').pipe(
      map((event) => ({email: event.target.value}))
    );
  }

  setInvalidState(node) {
    const errorMessage = document.getElementById(this.errorMessageId);

    node.setAttribute('aria-invalid', true);
    node.classList.add(this.errorCssClass);
    errorMessage.removeAttribute('hidden');
  }

  setValidState(node) {
    const errorMessage = document.getElementById(this.errorMessageId);

    node.removeAttribute('aria-invalid');
    node.classList.remove(this.errorCssClass);
    errorMessage.setAttribute('hidden', true);
  }

  /**
   * Validates the email input value against a predefined pattern.
   *
   * This function ensures that the email input value is cleaned by trimming
   * leading and trailing spaces and replacing multiple spaces with a single space.
   * The cleaned value is then updated in the UI and validated against the
   * `matchingPattern` regular expression.
   *
   * @returns {boolean} Returns `true` if the cleaned email value matches the pattern, otherwise `false`.
   */
  isValidEmailPattern() {
    const node = this.getNode();

    /* NHS20-1684: we verify that the value taken from the input it's clean,
    in case of spaces before and after the text, also is updated in the UI. */
    const cleanedValue = node.value.trim().replace(/\s+/g, ' ');
    node.value = cleanedValue;
    return emailAddressPattern.test(node.value);
  }

  validate() {
    const node = this.getNode();
    let isValid = this.isValidEmailPattern();

    return new Promise((resolve) => {
      if (isValid) {
        validateEmailAddress(node.value)
          .then((result) => {
            isValid = result;

            if (isValid) {
              this.setValidState(node);
            } else {
              this.setInvalidState(node);
            }

            resolve(isValid);
          })
          .catch(() => {
            this.setInvalidState(node);
            resolve(false);
          });
      } else {
        this.setInvalidState(node);
        resolve(false);
      }
    });
  }

}
